import { ReparoApi } from '@api';
import {
    type EntitySuggestionItem,
    type EntityTypeId,
    type EntityTypeMeta,
} from '@components/AnalystChat/ChatInput/types';

import { BASE_ENTITY_TYPE_META } from '@components/AnalystChat/ChatInput/utils';
import { AiEntityType } from '@diagon-alley/common';
import { useDebouncedValue } from '@mantine/hooks';
import {
    CubeIcon,
    PackageIcon,
    TagIcon,
    UsersThreeIcon,
} from '@phosphor-icons/react';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useReducer,
    useState,
} from 'react';
import EntityMentionContextProvider from './context';
import {
    ActionType,
    type EntityDataState,
    type EntityMentionContext as EntityMentionContextType,
    type EntityMentionState,
    type PaginationState,
} from './types';

const ITEMS_PER_PAGE = 10;

const buildPaginatedUrl = (base: string, page: number) => {
    const url = new URL(base, 'http://localhost'); // base placeholder for query building
    url.searchParams.set('page', page.toString());
    url.searchParams.set('limit', ITEMS_PER_PAGE.toString());
    // drop origin placeholder
    return `${url.pathname}${url.search}`;
};

type WarehouseResponse = {
    warehouseId?: string;
    name?: string;
    city?: string;
};

type ChannelResponse = {
    channelId?: string;
    name?: string;
    model?: string;
    abbreviationKey?: string;
};

type VendorResponse = {
    vendorId?: string;
    name?: string;
    type?: string;
};

type ProductResponse = {
    productId?: string;
    name?: string;
    productType?: string | null;
    sku?: string | null;
};

type SchemaColumnResponse = {
    id: string;
    label?: string;
    description?: string;
};

type EntitySearchApiResponse =
    | EntitySuggestionItem[]
    | {
          results: EntitySuggestionItem[];
          debugQuery?: string;
          generatedQuery?: string;
          executedQuery?: string;
          rawOutput?: string;
      };

type SchemaTableResponse = {
    id: string;
    label?: string;
    description?: string;
    typeId: string;
    columns: SchemaColumnResponse[];
};

const createEmptyPaginationState = (): PaginationState => ({
    currentPage: 1,
    perPage: ITEMS_PER_PAGE,
    totalItems: 0,
    hasMore: false,
});

const createEmptyEntityState = (): EntityDataState<EntitySuggestionItem> => ({
    data: [],
    pagination: createEmptyPaginationState(),
    isLoading: false,
    error: null,
});

const PRODUCT_ENTITY_TYPE: EntityTypeId = 'product';
const PRODUCT_CATEGORY_ENTITY_TYPE: EntityTypeId = 'productCategory';
const PRODUCT_TYPE_ENTITY_TYPE: EntityTypeId = 'productType';

const baseEntityMeta: EntityTypeMeta = {
    ...BASE_ENTITY_TYPE_META,
    vendor: {
        label: 'Vendors',
        icon: UsersThreeIcon,
    },
    [PRODUCT_CATEGORY_ENTITY_TYPE]: {
        label: 'Product Categories',
        icon: TagIcon,
    },
    [PRODUCT_TYPE_ENTITY_TYPE]: {
        label: 'Product Types',
        icon: CubeIcon,
    },
    [PRODUCT_ENTITY_TYPE]: {
        label: 'Products',
        icon: PackageIcon,
    },
};

const LOCAL_ONLY_ENTITY_TYPES = new Set<EntityTypeId>([
    PRODUCT_ENTITY_TYPE,
    PRODUCT_CATEGORY_ENTITY_TYPE,
    PRODUCT_TYPE_ENTITY_TYPE,
]);

const createInitialEntities = (): Record<
    EntityTypeId,
    EntityDataState<EntitySuggestionItem>
> => ({
    [AiEntityType.WAREHOUSE]: createEmptyEntityState(),
    [AiEntityType.CHANNEL]: createEmptyEntityState(),
    vendor: createEmptyEntityState(),
    [PRODUCT_ENTITY_TYPE]: createEmptyEntityState(),
    [PRODUCT_CATEGORY_ENTITY_TYPE]: createEmptyEntityState(),
    [PRODUCT_TYPE_ENTITY_TYPE]: createEmptyEntityState(),
});

const initialState: EntityMentionState = {
    selectedEntityType: null,
    entities: createInitialEntities(),
    genericSearchResults: [],
    genericSearchLoading: false,
    mentionSuggestionOpen: false,
    searchQuery: '',
    entityMeta: baseEntityMeta,
    availableEntityTypes: Object.keys(baseEntityMeta) as EntityTypeId[],
};

interface SetSelectedEntityTypeAction {
    type: ActionType.SET_SELECTED_ENTITY_TYPE;
    payload: EntityTypeId | null;
}

interface SetEntitiesAction {
    type: ActionType.SET_ENTITIES;
    entityType: EntityTypeId;
    data: EntitySuggestionItem[];
    pagination: Partial<PaginationState>;
    append?: boolean;
}

interface SetEntityLoadingAction {
    type: ActionType.SET_ENTITY_LOADING;
    entityType: EntityTypeId;
    isLoading: boolean;
}

interface SetEntityErrorAction {
    type: ActionType.SET_ENTITY_ERROR;
    entityType: EntityTypeId;
    error: Error | null;
}

interface SetSearchQueryAction {
    type: ActionType.SET_SEARCH_QUERY;
    payload: string;
}

interface SetGenericSearchResultsAction {
    type: ActionType.SET_GENERIC_SEARCH_RESULTS;
    payload: EntitySuggestionItem[];
}

interface ResetEntityDataAction {
    type: ActionType.RESET_ENTITY_DATA;
    payload: EntityTypeId;
}

interface SetMentionSuggestionOpenAction {
    type: ActionType.SET_MENTION_SUGGESTION_OPEN;
    payload: boolean;
}

interface SetEntityMetaAction {
    type: ActionType.SET_ENTITY_META;
    payload: EntityTypeMeta;
}

type Action =
    | SetSelectedEntityTypeAction
    | SetEntitiesAction
    | SetEntityLoadingAction
    | SetEntityErrorAction
    | SetSearchQueryAction
    | SetGenericSearchResultsAction
    | ResetEntityDataAction
    | SetMentionSuggestionOpenAction
    | SetEntityMetaAction;

const ensureEntityState = (
    entities: EntityMentionState['entities'],
    entityType: EntityTypeId,
) => {
    return entities[entityType] ?? createEmptyEntityState();
};

function reducer(
    reducerState: EntityMentionState,
    action: Action,
): EntityMentionState {
    switch (action.type) {
        case ActionType.SET_SELECTED_ENTITY_TYPE: {
            return {
                ...reducerState,
                selectedEntityType: action.payload,
                searchQuery: '', // Reset search when changing entity type
            };
        }

        case ActionType.SET_ENTITIES: {
            const { entityType, data, pagination, append } = action;
            const currentEntityData = ensureEntityState(
                reducerState.entities,
                entityType,
            );

            return {
                ...reducerState,
                entities: {
                    ...reducerState.entities,
                    [entityType]: {
                        ...currentEntityData,
                        data: append
                            ? [...currentEntityData.data, ...data]
                            : data,
                        pagination: {
                            ...createEmptyPaginationState(),
                            ...currentEntityData.pagination,
                            ...pagination,
                        },
                    },
                },
            };
        }

        case ActionType.SET_ENTITY_LOADING: {
            const { entityType, isLoading } = action;
            return {
                ...reducerState,
                entities: {
                    ...reducerState.entities,
                    [entityType]: {
                        ...ensureEntityState(reducerState.entities, entityType),
                        isLoading,
                    },
                },
            };
        }

        case ActionType.SET_ENTITY_ERROR: {
            const { entityType, error } = action;
            return {
                ...reducerState,
                entities: {
                    ...reducerState.entities,
                    [entityType]: {
                        ...ensureEntityState(reducerState.entities, entityType),
                        error,
                    },
                },
            };
        }

        case ActionType.SET_SEARCH_QUERY: {
            return {
                ...reducerState,
                searchQuery: action.payload,
            };
        }

        case ActionType.SET_GENERIC_SEARCH_RESULTS: {
            return {
                ...reducerState,
                genericSearchResults: action.payload,
            };
        }

        case ActionType.RESET_ENTITY_DATA: {
            return {
                ...reducerState,
                entities: {
                    ...reducerState.entities,
                    [action.payload]: {
                        ...createEmptyEntityState(),
                    },
                },
            };
        }

        case ActionType.SET_MENTION_SUGGESTION_OPEN: {
            return {
                ...reducerState,
                mentionSuggestionOpen: action.payload,
            };
        }

        case ActionType.SET_ENTITY_META: {
            const mergedMeta = {
                ...reducerState.entityMeta,
                ...action.payload,
            };
            return {
                ...reducerState,
                entityMeta: mergedMeta,
                availableEntityTypes: Object.keys(mergedMeta) as EntityTypeId[],
            };
        }

        default: {
            throw new Error('Unexpected action');
        }
    }
}

const EntityMentionProvider: React.FC<React.PropsWithChildren> = ({
    children,
}) => {
    const [reducerState, dispatch] = useReducer(reducer, initialState);
    const [debouncedSearchQuery] = useDebouncedValue(
        reducerState.searchQuery,
        500,
    );

    const [genericSearchLoading, setGenericSearchLoading] = useState(false);
    const [schemaTables, setSchemaTables] = useState<SchemaTableResponse[]>([]);

    const selectedEntityTypeRef = React.useRef<EntityTypeId | null>(
        reducerState.selectedEntityType,
    );
    const mentionSuggestionOpenRef = React.useRef<boolean>(
        reducerState.mentionSuggestionOpen,
    );
    const genericSearchRequestIdRef = React.useRef<number>(0);
    const inflightEntityTypesRef = React.useRef<Set<EntityTypeId>>(
        new Set<EntityTypeId>(),
    );
    const entityCacheRef = React.useRef<
        Map<EntityTypeId, EntitySuggestionItem[]>
    >(new Map());
    const schemaLoadedRef = React.useRef<boolean>(false);

    useEffect(() => {
        selectedEntityTypeRef.current = reducerState.selectedEntityType;
    }, [reducerState.selectedEntityType]);

    useEffect(() => {
        mentionSuggestionOpenRef.current = reducerState.mentionSuggestionOpen;
    }, [reducerState.mentionSuggestionOpen]);

    useEffect(() => {
        const loadSchemaTables = async () => {
            if (schemaLoadedRef.current) return;
            try {
                const response = (await ReparoApi({
                    url: '/schema/tables',
                    method: 'GET',
                    body: undefined,
                })) as unknown as { tables?: SchemaTableResponse[] };

                if (!response?.tables) return;
                schemaLoadedRef.current = true;
                setSchemaTables(response.tables);

                // We keep schema metadata for internal use, but avoid adding
                // table:* types to the mention picker to prevent noisy column entries.
            } catch (error) {
                // silent fail for mention dropdown
                console.error('Failed to load schema tables', error);
            }
        };

        void loadSchemaTables();
    }, []);

    const setSelectedEntityType = useCallback(
        (entityType: EntityTypeId | null) => {
            dispatch({
                type: ActionType.SET_SELECTED_ENTITY_TYPE,
                payload: entityType,
            });
        },
        [],
    );

    const setSearchQuery = useCallback((query: string) => {
        dispatch({
            type: ActionType.SET_SEARCH_QUERY,
            payload: query,
        });
    }, []);

    const setMentionSuggestionOpen = useCallback((open: boolean) => {
        dispatch({
            type: ActionType.SET_MENTION_SUGGESTION_OPEN,
            payload: open,
        });
    }, []);

    const mapWarehouseToSuggestion = useCallback(
        (warehouse: WarehouseResponse): EntitySuggestionItem | null => {
            const id = warehouse.warehouseId ?? warehouse.name;
            const label = warehouse.name ?? warehouse.warehouseId;

            if (!id || !label) return null;

            return {
                id,
                label,
                type: AiEntityType.WAREHOUSE,
                description: warehouse.city,
            };
        },
        [],
    );

    const mapChannelToSuggestion = useCallback(
        (channel: ChannelResponse): EntitySuggestionItem | null => {
            const id =
                channel.abbreviationKey ?? channel.channelId ?? channel.name;
            const label =
                channel.abbreviationKey ?? channel.name ?? channel.channelId;

            if (!id || !label) return null;

            return {
                id,
                label,
                type: AiEntityType.CHANNEL,
                description: channel.model,
            };
        },
        [],
    );

    const mapVendorToSuggestion = useCallback(
        (vendor: VendorResponse): EntitySuggestionItem | null => {
            const id = vendor.vendorId ?? vendor.name;
            const label = vendor.name ?? vendor.vendorId;
            if (!id || !label) return null;
            return {
                id,
                label,
                type: 'vendor',
                description: vendor.type,
            };
        },
        [],
    );

    const mapCategoryToSuggestion = useCallback(
        (category?: string | null): EntitySuggestionItem | null => {
            const label = category?.toString().trim();
            if (!label) return null;
            return {
                id: label,
                label,
                type: PRODUCT_CATEGORY_ENTITY_TYPE,
            };
        },
        [],
    );

    const mapProductTypeToSuggestion = useCallback(
        (productType?: string | null): EntitySuggestionItem | null => {
            const label = productType?.toString().trim();
            if (!label) return null;
            return {
                id: label,
                label,
                type: PRODUCT_TYPE_ENTITY_TYPE,
            };
        },
        [],
    );

    const mapProductToSuggestion = useCallback(
        (product: ProductResponse): EntitySuggestionItem | null => {
            const id = product.productId ?? product.sku ?? product.name;
            const label = product.name ?? product.sku ?? product.productId;
            if (!id || !label) return null;
            return {
                id,
                label,
                type: PRODUCT_ENTITY_TYPE,
                description: product.sku || product.productId,
            };
        },
        [],
    );

    const mapTableColumnsToSuggestions = useCallback(
        (table: SchemaTableResponse): EntitySuggestionItem[] => {
            return table.columns
                .map((column) => {
                    const label = column.label ?? column.id;
                    return {
                        id: `${table.id}.${column.id}`,
                        label,
                        type: table.typeId,
                        description:
                            column.description ??
                            `${table.label ?? table.id}.${column.id}`,
                    };
                })
                .filter(Boolean) as EntitySuggestionItem[];
        },
        [],
    );

    const getPageData = useCallback(
        (list: EntitySuggestionItem[], page: number) => {
            const start = (page - 1) * ITEMS_PER_PAGE;
            const end = start + ITEMS_PER_PAGE;
            const slice = list.slice(start, end);
            const hasMore = end < list.length;
            return { slice, hasMore, total: list.length };
        },
        [],
    );

    const fetchEntities = useCallback(
        async (entityType: EntityTypeId, page: number = 1) => {
            if (
                typeof entityType === 'string' &&
                entityType.startsWith('table:')
            ) {
                // Columns are intentionally hidden from the mention UI
                return;
            }

            if (inflightEntityTypesRef.current.has(entityType)) {
                return;
            }

            // Serve from cache if it already contains enough items for this page
            const cached = entityCacheRef.current.get(entityType) ?? [];
            const needed = page * ITEMS_PER_PAGE;
            if (cached.length >= needed) {
                const { slice, hasMore, total } = getPageData(cached, page);
                dispatch({
                    type: ActionType.SET_ENTITIES,
                    entityType,
                    data: slice,
                    pagination: {
                        currentPage: page,
                        perPage: ITEMS_PER_PAGE,
                        totalItems: total,
                        hasMore,
                    },
                    append: page > 1,
                });
                return;
            }

            inflightEntityTypesRef.current.add(entityType);

            dispatch({
                type: ActionType.SET_ENTITY_LOADING,
                entityType,
                isLoading: true,
            });

            try {
                if (entityType === AiEntityType.WAREHOUSE) {
                    const warehouses =
                        ((await ReparoApi({
                            url: buildPaginatedUrl('/warehouses', page),
                            method: 'GET',
                            body: undefined,
                        })) as unknown as WarehouseResponse[] | null) ?? [];

                    const suggestions = warehouses
                        .map(mapWarehouseToSuggestion)
                        .filter((item): item is EntitySuggestionItem =>
                            Boolean(item),
                        );

                    dispatch({
                        type: ActionType.SET_ENTITIES,
                        entityType,
                        data:
                            page > 1
                                ? [...cached, ...suggestions]
                                : suggestions,
                        pagination: {
                            currentPage: page,
                            perPage: ITEMS_PER_PAGE,
                            totalItems:
                                (page > 1 ? cached.length : 0) +
                                suggestions.length,
                            hasMore: suggestions.length === ITEMS_PER_PAGE,
                        },
                        append: page > 1,
                    });
                    entityCacheRef.current.set(
                        entityType,
                        page > 1 ? [...cached, ...suggestions] : suggestions,
                    );
                    return;
                }

                if (entityType === AiEntityType.CHANNEL) {
                    const channels =
                        ((await ReparoApi({
                            url: '/channels?page=1&limit=1000',
                            method: 'GET',
                            body: undefined,
                        })) as unknown as ChannelResponse[] | null) ?? [];

                    // Filter for distinct channel_abbr_key
                    const seenKeys = new Set<string>();
                    const suggestions = channels
                        .map(mapChannelToSuggestion)
                        .filter((item): item is EntitySuggestionItem => {
                            if (!item) return false;
                            // Deduplicate based on ID (which is abbreviationKey where possible)
                            if (seenKeys.has(item.id)) return false;
                            seenKeys.add(item.id);
                            return true;
                        });

                    dispatch({
                        type: ActionType.SET_ENTITIES,
                        entityType,
                        data: suggestions,
                        pagination: {
                            currentPage: 1,
                            perPage: suggestions.length,
                            totalItems: suggestions.length,
                            hasMore: false,
                        },
                        append: false,
                    });
                    entityCacheRef.current.set(entityType, suggestions);
                    return;
                }

                if (entityType === 'vendor') {
                    const vendors =
                        ((await ReparoApi({
                            url: buildPaginatedUrl('/vendors', page),
                            method: 'GET',
                            body: undefined,
                        })) as unknown as VendorResponse[] | null) ?? [];

                    const suggestions = vendors
                        .map(mapVendorToSuggestion)
                        .filter((item): item is EntitySuggestionItem =>
                            Boolean(item),
                        );

                    dispatch({
                        type: ActionType.SET_ENTITIES,
                        entityType,
                        data:
                            page > 1
                                ? [...cached, ...suggestions]
                                : suggestions,
                        pagination: {
                            currentPage: page,
                            perPage: ITEMS_PER_PAGE,
                            totalItems:
                                (page > 1 ? cached.length : 0) +
                                suggestions.length,
                            hasMore: suggestions.length === ITEMS_PER_PAGE,
                        },
                        append: page > 1,
                    });
                    entityCacheRef.current.set(
                        entityType,
                        page > 1 ? [...cached, ...suggestions] : suggestions,
                    );
                    return;
                }

                if (entityType === PRODUCT_CATEGORY_ENTITY_TYPE) {
                    const categories =
                        ((await ReparoApi({
                            url: '/products/categories/list',
                            method: 'POST',
                            body: null,
                        })) as unknown as
                            | (string | null | undefined)[]
                            | null) ?? [];

                    const suggestions = categories
                        .map(mapCategoryToSuggestion)
                        .filter((item): item is EntitySuggestionItem =>
                            Boolean(item),
                        )
                        .sort((a, b) => a.label.localeCompare(b.label));

                    // Load all categories at once (no pagination)
                    dispatch({
                        type: ActionType.SET_ENTITIES,
                        entityType,
                        data: suggestions,
                        pagination: {
                            currentPage: 1,
                            perPage: suggestions.length,
                            totalItems: suggestions.length,
                            hasMore: false,
                        },
                        append: false,
                    });
                    entityCacheRef.current.set(entityType, suggestions);
                    return;
                }

                if (entityType === PRODUCT_TYPE_ENTITY_TYPE) {
                    const productTypes =
                        ((await ReparoApi({
                            url: '/products/types/list',
                            method: 'POST',
                            body: undefined,
                        })) as unknown as
                            | (string | null | undefined)[]
                            | null) ?? [];

                    const suggestions = productTypes
                        .map(mapProductTypeToSuggestion)
                        .filter((item): item is EntitySuggestionItem =>
                            Boolean(item),
                        )
                        .sort((a, b) => a.label.localeCompare(b.label));

                    const { slice, hasMore, total } = getPageData(
                        suggestions,
                        page,
                    );

                    dispatch({
                        type: ActionType.SET_ENTITIES,
                        entityType,
                        data: page > 1 ? [...cached, ...slice] : slice,
                        pagination: {
                            currentPage: page,
                            perPage: ITEMS_PER_PAGE,
                            totalItems: total,
                            hasMore,
                        },
                        append: page > 1,
                    });
                    entityCacheRef.current.set(
                        entityType,
                        page > 1 ? [...cached, ...suggestions] : suggestions,
                    );
                    return;
                }

                if (entityType === PRODUCT_ENTITY_TYPE) {
                    const products =
                        ((await ReparoApi({
                            url: '/products/list',
                            method: 'POST',
                            body: undefined,
                        })) as unknown as ProductResponse[] | null) ?? [];

                    const suggestions = products
                        .map(mapProductToSuggestion)
                        .filter((item): item is EntitySuggestionItem =>
                            Boolean(item),
                        )
                        .sort((a, b) => a.label.localeCompare(b.label));

                    const { slice, hasMore, total } = getPageData(
                        suggestions,
                        page,
                    );

                    dispatch({
                        type: ActionType.SET_ENTITIES,
                        entityType,
                        data: page > 1 ? [...cached, ...slice] : slice,
                        pagination: {
                            currentPage: page,
                            perPage: ITEMS_PER_PAGE,
                            totalItems: total,
                            hasMore,
                        },
                        append: page > 1,
                    });
                    entityCacheRef.current.set(
                        entityType,
                        page > 1 ? [...cached, ...suggestions] : suggestions,
                    );
                    return;
                }

                if (
                    typeof entityType === 'string' &&
                    entityType.startsWith('table:')
                ) {
                    const tableId = entityType.replace('table:', '');
                    const table = schemaTables.find(
                        (tbl) => tbl.id === tableId,
                    );
                    if (table) {
                        const suggestions = mapTableColumnsToSuggestions(table);
                        const { slice, hasMore, total } = getPageData(
                            suggestions,
                            page,
                        );
                        dispatch({
                            type: ActionType.SET_ENTITIES,
                            entityType,
                            data: page > 1 ? [...cached, ...slice] : slice,
                            pagination: {
                                currentPage: page,
                                perPage: ITEMS_PER_PAGE,
                                totalItems:
                                    (page > 1 ? cached.length : 0) +
                                    slice.length,
                                hasMore,
                            },
                            append: page > 1,
                        });
                        entityCacheRef.current.set(
                            entityType,
                            page > 1 ? [...cached, ...slice] : slice,
                        );
                    }
                    return;
                }
            } catch (error) {
                dispatch({
                    type: ActionType.SET_ENTITY_ERROR,
                    entityType,
                    error:
                        error instanceof Error
                            ? error
                            : new Error('Unknown error'),
                });
            } finally {
                dispatch({
                    type: ActionType.SET_ENTITY_LOADING,
                    entityType,
                    isLoading: false,
                });
                inflightEntityTypesRef.current.delete(entityType);
            }
        },
        [
            mapChannelToSuggestion,
            mapCategoryToSuggestion,
            mapProductToSuggestion,
            mapProductTypeToSuggestion,
            mapVendorToSuggestion,
            mapWarehouseToSuggestion,
            mapTableColumnsToSuggestions,
            getPageData,
            schemaTables,
        ],
    );

    const loadMoreEntities = useCallback(
        async (entityType: EntityTypeId) => {
            const currentEntityState =
                reducerState.entities[entityType] ?? createEmptyEntityState();
            if (
                !currentEntityState.pagination.hasMore ||
                currentEntityState.isLoading
            ) {
                return;
            }

            const nextPage = currentEntityState.pagination.currentPage + 1;
            await fetchEntities(entityType, nextPage);
        },
        [reducerState.entities, fetchEntities],
    );

    useEffect(() => {
        if (
            reducerState.selectedEntityType &&
            reducerState.mentionSuggestionOpen &&
            !entityCacheRef.current.has(reducerState.selectedEntityType)
        ) {
            void fetchEntities(reducerState.selectedEntityType);
        }
    }, [
        reducerState.selectedEntityType,
        reducerState.mentionSuggestionOpen,
        fetchEntities,
    ]);

    useEffect(() => {
        const query = debouncedSearchQuery.trim();
        const selectedType = selectedEntityTypeRef.current;
        const isLocalType =
            selectedType && LOCAL_ONLY_ENTITY_TYPES.has(selectedType);

        if (!mentionSuggestionOpenRef.current) {
            return;
        }

        if (!query) {
            dispatch({
                type: ActionType.SET_GENERIC_SEARCH_RESULTS,
                payload: [],
            });
            setGenericSearchLoading(false);

            // restore cached page 1 for selected type when clearing search
            if (selectedType && entityCacheRef.current.has(selectedType)) {
                const cached = entityCacheRef.current.get(selectedType) ?? [];
                const { slice, hasMore, total } = getPageData(cached, 1);
                dispatch({
                    type: ActionType.SET_ENTITIES,
                    entityType: selectedType,
                    data: slice,
                    pagination: {
                        currentPage: 1,
                        perPage: ITEMS_PER_PAGE,
                        totalItems: total,
                        hasMore,
                    },
                    append: false,
                });
            }
            return;
        }

        const requestId = genericSearchRequestIdRef.current + 1;
        genericSearchRequestIdRef.current = requestId;

        setGenericSearchLoading(true);
        if (selectedType) {
            dispatch({
                type: ActionType.SET_ENTITY_LOADING,
                entityType: selectedType,
                isLoading: true,
            });
        }

        if (isLocalType && selectedType) {
            const cached = entityCacheRef.current.get(selectedType) ?? [];
            const filtered = cached.filter((item) =>
                item.label.toLowerCase().includes(query.toLowerCase()),
            );

            dispatch({
                type: ActionType.SET_ENTITIES,
                entityType: selectedType,
                data: filtered,
                pagination: {
                    currentPage: 1,
                    perPage: ITEMS_PER_PAGE,
                    totalItems: filtered.length,
                    hasMore: false,
                },
                append: false,
            });

            setGenericSearchLoading(false);
            dispatch({
                type: ActionType.SET_ENTITY_LOADING,
                entityType: selectedType,
                isLoading: false,
            });
            return;
        }

        void (async () => {
            try {
                const typeParam =
                    selectedType && !String(selectedType).startsWith('table:')
                        ? `&type=${encodeURIComponent(selectedType)}`
                        : '';
                const response =
                    ((await ReparoApi({
                        url: `/entity-search?q=${encodeURIComponent(
                            query,
                        )}${typeParam}&debug=1`,
                        method: 'GET',
                        body: undefined,
                    })) as unknown as EntitySearchApiResponse | null) ?? [];

                const results = Array.isArray(response)
                    ? response
                    : (response?.results ?? []);

                if (!Array.isArray(response)) {
                    const { generatedQuery, executedQuery, rawOutput } =
                        response ?? {};
                    if (generatedQuery || executedQuery) {
                        // eslint-disable-next-line no-console
                        console.info(
                            '[entity-search][debug] generated:',
                            generatedQuery,
                            '| executed:',
                            executedQuery,
                        );
                    }
                    if (rawOutput) {
                        // eslint-disable-next-line no-console
                        console.debug(
                            '[entity-search][debug] raw LLM output:',
                            rawOutput,
                        );
                    }
                }

                if (genericSearchRequestIdRef.current !== requestId) return;

                if (selectedType) {
                    const filtered = results.filter(
                        (item) => item.type === selectedType,
                    );
                    const dataToUse = filtered.length > 0 ? filtered : results;
                    dispatch({
                        type: ActionType.SET_ENTITIES,
                        entityType: selectedType,
                        data: dataToUse,
                        pagination: {
                            currentPage: 1,
                            perPage: ITEMS_PER_PAGE,
                            totalItems: dataToUse.length,
                            hasMore: false,
                        },
                        append: false,
                    });
                } else {
                    dispatch({
                        type: ActionType.SET_GENERIC_SEARCH_RESULTS,
                        payload: results,
                    });
                }
            } catch (error) {
                if (!selectedType) {
                    dispatch({
                        type: ActionType.SET_GENERIC_SEARCH_RESULTS,
                        payload: [],
                    });
                }
            } finally {
                if (genericSearchRequestIdRef.current === requestId) {
                    setGenericSearchLoading(false);
                }
                if (selectedType) {
                    dispatch({
                        type: ActionType.SET_ENTITY_LOADING,
                        entityType: selectedType,
                        isLoading: false,
                    });
                }
            }
        })();
    }, [debouncedSearchQuery, getPageData]);

    const state = useMemo(
        () => ({
            ...reducerState,
            genericSearchLoading,
        }),
        [genericSearchLoading, reducerState],
    );

    const actions = useMemo(
        () => ({
            setSelectedEntityType,
            setSearchQuery,
            setMentionSuggestionOpen,
            fetchEntities,
            loadMoreEntities,
        }),
        [
            setSelectedEntityType,
            setSearchQuery,
            setMentionSuggestionOpen,
            fetchEntities,
            loadMoreEntities,
        ],
    );

    const value: EntityMentionContextType = useMemo(
        () => ({
            state,
            actions,
        }),
        [state, actions],
    );

    return (
        <EntityMentionContextProvider.Provider value={value}>
            {children}
        </EntityMentionContextProvider.Provider>
    );
};

export default EntityMentionProvider;
